// Shared scaffold for the per-group admin "Members" section (circles,
// networks, spaces). The list/table shape is identical across all three —
// what differs is the action menu and what happens on each action. Each
// surface composes its own role/action cells and passes them to
// `buildMemberColumns`. Roles are flat — every `role='leader'` row reads
// as "Leader"; there is no founder / primary / co-leader distinction.

import { type ReactNode } from 'react';
import {
  Avatar,
  Body,
  Caption,
  Card,
  Pill,
  Row,
  Skeleton,
  Stack,
  type ColumnDef,
} from '@leanin/ui';

import type { GroupMemberView } from '../../../lib/views/groups';
import { formatMonthYear } from '../../../lib/format-date';
import styles from './members-table.module.css';

export function memberName(m: GroupMemberView): string {
  return `${m.firstName} ${m.lastName}`.trim() || 'Member';
}

export function memberSublabel(m: GroupMemberView): string {
  return [m.jobTitle, m.company].filter(Boolean).join(' · ') || 'Member';
}

// Case-insensitive substring match on name / job title / company. Used by
// every member section's search box — the search is client-side because
// the API hands back the full membership at once (pageSize: 100).
export function filterMembersBySearch<T extends GroupMemberView>(
  members: ReadonlyArray<T>,
  search: string,
): ReadonlyArray<T> {
  const q = search.trim().toLowerCase();
  if (q.length === 0) return members;
  return members.filter(
    (m) =>
      memberName(m).toLowerCase().includes(q) ||
      (m.jobTitle ?? '').toLowerCase().includes(q) ||
      (m.company ?? '').toLowerCase().includes(q),
  );
}

export type MemberColumnsOptions<T extends GroupMemberView> = {
  readonly viewerUserId: string;
  /** Role-cell renderer — every surface renders the same flat
   * "Leader"/"Member" labels; the prop hangs around so a per-kind variant
   * can swap colours if needed. */
  readonly renderRole: (m: T) => ReactNode;
  /** Action-cell renderer for non-viewer rows. Return `null` to omit the
   * actions column entirely (e.g. when a surface has no per-row actions). */
  readonly renderActions:
    | ((m: T, ctx: { readonly isYou: boolean; readonly name: string }) => ReactNode)
    | null;
};

export function buildMemberColumns<T extends GroupMemberView>(
  opts: MemberColumnsOptions<T>,
): ColumnDef<T>[] {
  const cols: ColumnDef<T>[] = [
    {
      id: 'member',
      header: 'Member',
      meta: { mobilePrimary: true },
      cell: ({ row }) => {
        const m = row.original;
        const name = memberName(m);
        const isYou = m.userId === opts.viewerUserId;
        return (
          <Row gap="md" align="center" style={{ minWidth: 0 }}>
            <Avatar src={m.photoUrl ?? undefined} name={name} size={36} />
            <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
              <Row gap="xs" align="center" style={{ minWidth: 0 }}>
                <Body size="md">{name}</Body>
                {isYou ? <Pill tone="burgundy">You</Pill> : null}
              </Row>
              <Caption>{memberSublabel(m)}</Caption>
              {/* On mobile the role/joined/actions columns are hidden; show them
                  inline here so the card row stays compact. Plain div so the
                  CSS display:none isn't overridden by Row's inline flex style. */}
              <div className={styles.mobileMetaRow}>
                {opts.renderRole(m)}
                <Caption>{formatMonthYear(m.joinedAt)}</Caption>
                {opts.renderActions !== null && !isYou ? (
                  <div style={{ marginInlineStart: 'auto' }}>
                    {opts.renderActions(m, { isYou, name })}
                  </div>
                ) : null}
              </div>
            </Stack>
          </Row>
        );
      },
    },
    {
      id: 'role',
      header: 'Role',
      meta: { width: 160, nowrap: true, mobileHide: true },
      cell: ({ row }) => opts.renderRole(row.original),
    },
    {
      id: 'joined',
      header: 'Joined',
      meta: { width: 140, nowrap: true, mobileHide: true },
      cell: ({ row }) => (
        <Body size="sm" color="mute">
          {formatMonthYear(row.original.joinedAt)}
        </Body>
      ),
    },
  ];
  if (opts.renderActions !== null) {
    const renderActions = opts.renderActions;
    cols.push({
      id: 'actions',
      header: '',
      meta: { width: 100, align: 'end', nowrap: true, mobileHide: true },
      cell: ({ row }) => {
        const m = row.original;
        const name = memberName(m);
        const isYou = m.userId === opts.viewerUserId;
        if (isYou) {
          return (
            <Body size="sm" color="mute">
              —
            </Body>
          );
        }
        return renderActions(m, { isYou, name });
      },
    });
  }
  return cols;
}

// Loading-state surrogate while the membership query is in flight.
// Matches the migrated DataTable layout: skeleton header → skeleton rows
// inside a Card+border. Replaces the old AdminMembersListSkeleton.
export function MembersTableSkeleton({
  rows = 6,
  withActions = true,
}: {
  rows?: number;
  withActions?: boolean;
}) {
  return (
    <Stack gap="md">
      <Skeleton width={320} height={36} rounded="md" />
      <Card pad="flush" style={{ overflow: 'hidden' }}>
        <Stack gap="none">
          {Array.from({ length: rows }, (_, i) => (
            <Row
              key={i}
              gap="md"
              align="center"
              py="md"
              px="lg"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <Skeleton width={36} height={36} rounded="full" />
              <Stack gap="xs" style={{ flex: 1 }}>
                <Skeleton width={140} height={14} rounded="sm" />
                <Skeleton width={200} height={12} rounded="sm" />
              </Stack>
              <Skeleton width={88} height={20} rounded="full" />
              <Skeleton width={80} height={14} rounded="sm" />
              {withActions ? <Skeleton width={72} height={28} rounded="md" /> : null}
            </Row>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}
